<template>
  <b-card>
    <div class="d-flex align-items-center justify-content-between">
      <h3>Create new ride request</h3>
      <b-button
        variant="primary"
        :disabled="creatingTrip || canSubmit"
        @click="createTrip"
      >
        <b-spinner
          v-if="creatingTrip"
          small
        />
        <span v-else>Create Trip</span>
      </b-button>
    </div>
    <b-row>
      <b-col md="6">
        <b-form-group label="Search user by phone no.">
          <UsersSearch
            ref="usersSearchRef"
            :auto-reset="false"
            @users-selection="selectUserHandler"
          />
        </b-form-group>
      </b-col>
      <b-col md="6">
        <ZonesList
          ref="zonesRef"
          @zone-selected="tripDetails.zone = $event"
        />
      </b-col>
      <b-col
        md="6"
      >
        <b-form-group label="Location Type">
          <b-form-radio-group
            v-model="radioSelection"
            class="mb-2"
          >
            <b-form-radio :value="PICKUP">
              <span class="mr-1">Pickup</span>
              <img
                style="height:25px"
                :src="Man"
              >
            </b-form-radio>
            <b-form-radio
              :disabled="!pickupMarker"
              :value="DROPOFF"
            >
              <span class="mr-1">Dropoff</span>
              <img
                style="height:25px"
                :src="PetraP"
              >
            </b-form-radio>
          </b-form-radio-group>
          <v-select
            v-model="selectedPlace"
            :placeholder="`Search ${radioSelection == PICKUP ? 'Pick-up' : 'Drop-off'}`"
            label="description"
            :options="places"
            class="mb-2"
            clear-search-on-select
            @search="fetchPlaces"
            @input="onSelectedPlace"
          />
          <div
            v-if="pickupAddress"
            class="d-flex align-items-center mb-1"
          >
            <img
              class="mr-1"
              style="height:25px"
              :src="Man"
            >
            {{ pickupAddress }}
          </div>
          <div
            v-if="dropoffAddress"
            class="d-flex align-items-center mb-1"
          >
            <img
              class="mr-1"
              style="height:25px"
              :src="PetraP"
            >
            {{ dropoffAddress }}
          </div>
          <base-overlay :show="setMarkerLoading">
            <div id="map" />
          </base-overlay>
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="Services and eta">
          <base-overlay :show="loadingServices">
            <div
              v-for="(s, ix) in services"
              :key="ix"
              class="mb-4 p-2  trip-service__card"
              :class="{
                'trip-service__card-selected': tripDetails.eta && (s.service.id == tripDetails.eta.service.id)
              }"
              @click="selectService(s)"
            >
              <div class="d-flex justify-content-between mb-1">
                <div class="d-flex align-items-center">
                  <img
                    class="mr-1"
                    style="height:30px;"
                    :src="s.service.icon_url"
                  >
                  <h5>{{ s.service.class_label }}</h5>
                </div>
                <h6>Distance: <small>{{ s.distance ? s.distance : s.pickup_eta.distance }} KM</small></h6>
                <h6>Time: <small>{{ s.estimated_time ? s.estimated_time : s.pickup_eta.time_in_minutes }} Min</small></h6>
              </div>
              <div v-if="s.price_in_words">
                <h6>Price: <small class="text-success">{{ s.price_in_words }}</small></h6>
              </div>
            </div>
          </base-overlay>
        </b-form-group>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormRadioGroup, BFormRadio, BButton, BSpinner,
} from 'bootstrap-vue'
import { debounce } from 'lodash'
import vSelect from 'vue-select'
import {
  computed, onMounted, ref, watch,
} from '@vue/composition-api'
import axiosIns from '@/libs/axios'
import UsersSearch from '@/views/users/rider/components/UsersSearch.vue'
import ZonesList from '../components/create-trip/ZonesList.vue'
import BaseOverlay from '@/UI/BaseOverlay.vue'
import PetraP from '@/assets/images/icons/petra-p.svg'
import Man from '@/assets/images/icons/man.svg'
import { errorNotification, successNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BCard,
    BRow,
    ZonesList,
    BaseOverlay,
    BButton,
    BCol,
    BSpinner,
    BFormRadio,
    vSelect,
    BFormGroup,
    BFormRadioGroup,
    UsersSearch,
  },
  setup(_, ctx) {
    const PICKUP = 'pickup'
    const DROPOFF = 'dropoff'
    const radioSelection = ref(PICKUP)
    const tripDetails = ref({
      zone: null,
      pickup: null,
      dropoff: null,
      service: null,
      eta: null,
      user: null,
      paymentMethod: 'cash',
    })
    const map = ref()

    const pickupMarker = ref()
    const dropoffMarker = ref()
    const pickupAddress = ref('')
    const dropoffAddress = ref('')

    // eslint-disable-next-line no-undef
    const mapBounds = ref(new google.maps.LatLngBounds())

    const places = ref([])
    const selectedPlace = ref(null)
    const setMarkerLoading = ref(false)
    const fetchPlaces = debounce(async (search, loading) => {
      if (!search) return
      loading(true)
      const res = await axiosIns.get(`/google/search/${search}`)
      places.value = res.data.data.places
      loading(false)
    }, 1000)

    const services = ref([])
    const loadingServices = ref(false)
    const getServicesEta = async () => {
      try {
        loadingServices.value = true
        const { position } = pickupMarker.value
        const pickupCoords = {
          lat: position.lat(),
          lng: position.lng(),
        }
        const result = await axiosIns.get('/trips/eta-by-service', {
          params: {
            originLat: pickupCoords.lat,
            originLng: pickupCoords.lng,
            destinationLat: dropoffMarker.value ? dropoffMarker.value.position.lat() : undefined,
            destinationLng: dropoffMarker.value ? dropoffMarker.value.position.lng() : undefined,
          },
        })
        const { success, error_message: errMsg, data } = result.data.data
        if (!success) {
          errorNotification(errMsg || 'Out of zone')
          services.value = []
          return
        }
        const { eta } = data
        services.value = eta
      } catch (err) {
        const { error_message: errMsg } = err.response.data
        errorNotification(errMsg || 'Out of zone')
        services.value = []
      } finally {
        loadingServices.value = false
      }
    }

    const handleMarkerPosition = (coords, markerType, address) => {
      if (markerType === PICKUP) {
        pickupAddress.value = address
        if (pickupMarker.value) {
          pickupMarker.value.setPosition({
            lat: coords.lat,
            lng: coords.lng,
          })
        } else {
          // eslint-disable-next-line no-undef
          pickupMarker.value = new google.maps.Marker({
            position: {
              lat: coords.lat,
              lng: coords.lng,
            },
            icon: {
              url: Man,
              // eslint-disable-next-line no-undef
              scaledSize: new google.maps.Size(50, 50),
              // eslint-disable-next-line no-undef
              origin: new google.maps.Point(0, 0),
              // eslint-disable-next-line no-undef
              anchor: new google.maps.Point(30, 30),
            },
          })
        }
        map.value.setCenter({
          lat: coords.lat,
          lng: coords.lng,
        })
        pickupMarker.value.setMap(map.value)
      } else if (markerType === DROPOFF) {
        dropoffAddress.value = address
        if (dropoffMarker.value) {
          dropoffMarker.value.setPosition({
            lat: coords.lat,
            lng: coords.lng,
          })
        } else {
          // eslint-disable-next-line no-undef
          dropoffMarker.value = new google.maps.Marker({
            position: {
              lat: coords.lat,
              lng: coords.lng,
            },
            icon: {
              url: PetraP,
              // eslint-disable-next-line no-undef
              scaledSize: new google.maps.Size(50, 50),
              // eslint-disable-next-line no-undef
              origin: new google.maps.Point(0, 0),
              // eslint-disable-next-line no-undef
              anchor: new google.maps.Point(30, 30),
            },
          })
          dropoffMarker.value.addListener('click', () => {
            dropoffMarker.value.setMap(null)
            dropoffMarker.value = null
            dropoffAddress.value = ''
            getServicesEta()
          })
        }
        map.value.setCenter({
          lat: coords.lat,
          lng: coords.lng,
        })
        dropoffMarker.value.setMap(map.value)
      } else {
        console.error('MARKER TYPE NOT PROVIDED')
      }
      mapBounds.value.extend(
        // eslint-disable-next-line no-undef
        new google.maps.LatLng(
          coords.lat,
          coords.lng,
        ),
      )
      map.value.fitBounds(mapBounds.value)
      getServicesEta()
    }

    const onSelectedPlace = async evt => {
      const { description } = evt
      setMarkerLoading.value = true
      const res = await axiosIns.get(`/google/geocode/${selectedPlace.value.place_id}`)

      const { location } = res.data.data.results.at(0).geometry

      handleMarkerPosition(location, radioSelection.value, description)
      setMarkerLoading.value = false
      selectedPlace.value = null
    }

    watch(radioSelection, () => {
      places.value = []
      selectedPlace.value = null
    })

    const selectService = s => {
      tripDetails.value.eta = JSON.parse(JSON.stringify(s))
      tripDetails.value.service = {
        class: s.service.class,
        name: s.service.name,
      }
    }

    const initMap = () => {
      const center = {
        lat: 31.963158, lng: 35.930359,
      }
      // eslint-disable-next-line no-undef
      map.value = new google.maps.Map(document.getElementById('map'), {
        zoom: 15,
        center,
        draggableCursor: 'crosshair',
      })

      map.value.addListener('click', ev => {
        const clickedCoords = {
          lat: ev.latLng.lat(),
          lng: ev.latLng.lng(),
        }
        handleMarkerPosition(clickedCoords, radioSelection.value)
      })
    }
    onMounted(() => {
      initMap()
    })

    const selectUserHandler = e => {
      tripDetails.value.user = {
        user_id: e.id,
        external_id: e.externalId,
        phone_number: e.phoneNumber,
        first_name: e.firstName,
        email: e.email,
        name: e.firstName,
      }
    }

    const canSubmit = computed(() => !pickupMarker.value || !tripDetails.value.eta || !tripDetails.value.user || !tripDetails.value.zone || !tripDetails.value.service)

    const creatingTrip = ref(false)
    const createTrip = async () => {
      const { usersSearchRef, zonesRef } = ctx.refs
      const { position } = pickupMarker.value
      const pickupCoords = {
        lat: position.lat(),
        lon: position.lng(),
      }
      tripDetails.value.pickup = pickupCoords
      if (dropoffMarker.value) {
        const { position: dropoffPosition } = dropoffMarker.value
        const dropoffCoords = {
          lat: dropoffPosition.lat(),
          lon: dropoffPosition.lng(),
        }
        tripDetails.value.dropoff = dropoffCoords
      }
      const requestTemp = { ...tripDetails.value }
      if (!dropoffMarker.value) {
        delete requestTemp.dropoff
      }
      try {
        creatingTrip.value = true
        const result = await axiosIns.post('/trips', requestTemp)

        const { data } = result.data
        if (data?.success === false && data.error_message) {
          errorNotification(data?.error_message || 'An error occured..')
          return
        }
        radioSelection.value = PICKUP
        usersSearchRef.resetValue()
        zonesRef.resetValue()

        pickupMarker.value.setMap(null)
        pickupMarker.value = null
        if (dropoffMarker.value) {
          dropoffMarker.value.setMap(null)
          dropoffMarker.value = null
        }
        tripDetails.value.dropoff = null
        tripDetails.value.pickup = null
        tripDetails.value.zone = null
        tripDetails.value.service = null
        tripDetails.value.eta = null
        tripDetails.value.user = null
        pickupAddress.value = ''
        dropoffAddress.value = ''
        services.value = []
        // eslint-disable-next-line no-undef
        mapBounds.value = new google.maps.LatLngBounds()
        successNotification('Ride requested successfully!')
      } catch (error) {
        errorNotification('An error occured')
      } finally {
        creatingTrip.value = false
      }
    }

    return {
      radioSelection,
      tripDetails,
      onSelectedPlace,
      services,
      canSubmit,
      selectedPlace,
      selectService,
      selectUserHandler,
      PICKUP,
      createTrip,
      loadingServices,
      setMarkerLoading,
      pickupMarker,
      creatingTrip,
      DROPOFF,
      places,
      pickupAddress,
      dropoffAddress,
      fetchPlaces,
      PetraP,
      Man,
    }
  },
}
</script>

<style scoped>
#map {
    width:100%;
    height: 400px;
}
.trip-service__card {
    border: 1px solid lightblue;
    border-radius: 12px;
    cursor: pointer;
    transition: all 200ms
}
.trip-service__card:hover,
.trip-service__card-selected {
    background: lightblue
}
</style>
